import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TopicType } from '../../types';
import NewRegionSelect from '@/pages/home/components/NewRegionSelect';
import { commonBackInfo } from '../../utils';
import {
  topicLocale,
  topicRadioOptions,
  topicShortcutOptions,
} from '../../constants/topic';
import useDefaultTwoLevelFilter, {
  getLevelOneInfo,
} from '../../hooks/useDefaultTwoLevelFilter';
import { topicEnMap, topicZhMap } from '@/pages/opinion/constants';
import BubbleCommon, {
  filterTopicTipsText,
  summaryKeyTopicTips,
} from '../../../BubbleCommon';
import { addFilterBuriedPoint } from '../../utils/point';
import { FilterButtonPoint } from '@op/operationLog/pages/filter';
import { Segment } from 'tea-component';
import { useUpdateEffect } from 'ahooks';
import { topicPlaceholder } from '../../constants/placeholder';
import './index.scss';
import { LocaleType } from '@op/types/common';
import { useOptionsMemo } from '../../hooks/common';
import { uniqueArray } from '@op/utils';
import { RegionSelectButtonContextType } from '@home/components/NewRegionSelect/type';
import useLanguage from '@op/hooks/useLanguage';
import { useCategoryTab } from './useCategoryTab';
import ContentHeader from './ContentHeader';

const topicRuleTextMap = {
  include: 'Include',
  exclude: 'Exclude',
  only_include: 'Only Include',
  only_exclude: 'Exclude Only Contain',
};

type TTopicRule = 'include' | 'exclude' | 'only_include' | 'only_exclude';

export default function TopicSelect({
  onChange,
  defaultValue,
  filterValue,
  lang,
  filterOptions,
  unlabeledReviewsValue = 'other',
  beginWatch = false,
  languageTitle = topicLocale,
  backOneLevel = false,
  backAllLevel2Value = false,
  hasHeaderContent = true,
  hasAllOptions = true,
  hasAllKeyTopicSelectOptions = false,
  gameInfo,
  boxClassName,
  hasUnlabeledOptions = true,
  oriDefaultFilter,
  dealwithOptions,
  showShortcut = true,
  disabledOptions = [],
  disabledTips = null,
  hasCategoryTab = false,
  categoryOptions,
  onlyCategory = false,
}: TopicType) {
  const { formatMessage } = useLanguage();
  // ============ 默认 Tab 选择逻辑 ============
  const getDefaultTopicType = (): 'all_topic' | 'key_topic' | 'category' => {
    if (hasCategoryTab) {
      return 'category'; // 纯 Category 模式
    }
    if (hasAllKeyTopicSelectOptions) {
      return defaultValue?.topicType || 'all_topic'; // Topic 模式或混合模式
    }
    return 'all_topic';
  };

  const [topicType, setTopicType] = useState<
    'all_topic' | 'key_topic' | 'category'
  >(getDefaultTopicType());
  const [topicRule, setTopicRule] = useState<TTopicRule>(
    defaultValue?.topic_rule || 'include',
  );
  const topicGroupValue = useMemo(() => {
    return topicRule.includes('include') ? 'include' : 'exclude';
  }, [topicRule]);

  // ============ 初始化 Category Tab Hook ============
  const categoryTab = useCategoryTab({
    hasCategoryTab,
    categoryOptions,
    defaultValue,
    filterValue,
  });

  const topicTransMap = useRef<{
    [key: string]: { [key in LocaleType]: string };
  }>({});

  useUpdateEffect(() => {
    if (filterValue) {
      setTopicType(filterValue.topicType || topicType);
      setTopicRule(filterValue.topic_rule || topicRule);
    }
  }, [filterValue]);

  function getTextInfo(text: string, disabled: boolean) {
    if (disabled) {
      return (
        <BubbleCommon showIcon={false} tipsMap={disabledTips}>
          {text}
        </BubbleCommon>
      );
    }

    return text;
  }

  function getTopicOptions(topicType: 'key_topic' | 'all_topic' | 'category') {
    const filterMap: any = {};
    const { topic_trans, key_topic } = filterOptions || {
      topic_trans: [],
      key_topic: {},
    };
    const flag = topicType === 'key_topic';
    if (topic_trans?.length > 0) {
      let list = topic_trans;
      if (flag) {
        const twoLevelTopic = Object.values(key_topic || {}).flat(3);

        list = list.filter(({ topic_to_en }: any) => {
          return twoLevelTopic.includes(topic_to_en.toLocaleLowerCase());
        });
      }

      list.map(({ L1_topic, L1_topic_transl, topic_to_en, trans }: any) => {
        const key = L1_topic?.toLocaleLowerCase?.();
        if (!filterMap[key]) {
          filterMap[key] = {
            value: key,
            text: topicEnMap[key] || L1_topic,
            textCN: topicZhMap[key] || L1_topic_transl,
            children: [],
          };
        }
        const key1 = topic_to_en?.toLocaleLowerCase?.();
        const disabled = disabledOptions.includes(key1);
        filterMap[key].children.push({
          value: key1,
          text: getTextInfo(topicEnMap[key1] || topic_to_en, disabled),
          textCN: getTextInfo(topicZhMap[key1] || trans, disabled),
          disabled,
        });

        topicTransMap.current[key1] = {
          'zh-CN': topicZhMap[key1] || trans,
          'en-US': topicEnMap[key1] || topic_to_en,
        };
      });
    }

    const resultOptions = Object.values(filterMap);

    if (!flag && hasUnlabeledOptions) {
      resultOptions.push({
        text: 'Unlabeled reviews',
        textCN: '未标记的评论',
        value: unlabeledReviewsValue,
        children: [
          {
            text: 'Unlabeled reviews',
            textCN: '未标记的评论',
            value: unlabeledReviewsValue,
          },
        ],
      });

      topicTransMap.current[unlabeledReviewsValue] = {
        'zh-CN': '未标记的评论',
        'en-US': 'Unlabeled reviews',
      };
    }

    // 校验 resultOptions 中的 children 是否都是 disabled，如果是，则将当前对象也加上 disabled 属性
    resultOptions.forEach((option: any) => {
      if (option.children && option.children.length > 0) {
        const allChildrenDisabled = option.children.every(
          (child: any) => child.disabled === true,
        );
        if (allChildrenDisabled) {
          option.disabled = true;
          option.text = getTextInfo(option.text, true);
          option.textCN = getTextInfo(option.textCN, true);
        }
      }
    });

    return resultOptions;
  }

  const topicOptions: any = useMemo(() => {
    return getTopicOptions(topicType);
  }, [filterOptions, filterValue, topicType]);

  const topicOptionsList = useOptionsMemo({
    options: topicOptions,
    dealwithOptions,
    key: 'topic',
    deeps: [topicOptions],
  });

  const getAllTopicLevelTwoValue = (options: Array<any> = topicOptions) => {
    return (options || [])
      .map((item) => (item?.children || []).map((child) => child?.value))
      .flat()
      .filter((value) => value !== undefined);
  };

  // ============ Tab 切换处理 ============
  const handleTopicTypeChange = useCallback(
    (value: 'all_topic' | 'key_topic' | 'category') => {
      setTopicType(value);
    },
    [],
  );

  // ============ Segment 选项配置 ============
  const segmentOptions = useMemo(() => {
    const opts: Array<{ text: any; value: string }> = onlyCategory
      ? []
      : [{ text: formatMessage({ id: 'All Topic' }), value: 'all_topic' }];
    if (hasAllKeyTopicSelectOptions) {
      opts.push({
        text: (
          <div className="dbo-topic-key-topic">
            {formatMessage({ id: 'Key Topic' })}
            <BubbleCommon
              tipsMap={summaryKeyTopicTips}
              placement="top"
              selected={topicType === 'key_topic'}
            />
          </div>
        ),
        value: 'key_topic',
      });
    }
    if (hasCategoryTab) {
      opts.push({ text: formatMessage({ id: 'AI Topic' }), value: 'category' });
    }
    return opts;
  }, [hasAllKeyTopicSelectOptions, topicType, formatMessage]);

  // ============ Segment 显示逻辑：Tab 数量 > 1 时才显示 ============
  const showSegment = useMemo(() => {
    const tabCount = segmentOptions.length;
    return tabCount > 1;
  }, [segmentOptions]);

  const { shortcutOptions, radioOptions, hasShortcutOptions } = useMemo(() => {
    const gameId = gameInfo?.edition_unified_id as string;
    const getShortcutFunc = topicShortcutOptions[gameId];

    if (!getShortcutFunc)
      return {
        shortcutOptions: null,
        radioOptions: hasAllOptions ? topicRadioOptions : [],
        hasShortcutOptions: false,
      };

    return {
      shortcutOptions: [
        {
          textCN: '全部',
          text: 'All',
          type: 'All',
          filter: (options: any[]) => {
            return getAllTopicLevelTwoValue(options);
          },
        },
        getShortcutFunc(getAllTopicLevelTwoValue),
      ],
      radioOptions: [],
      hasShortcutOptions: showShortcut,
    };
  }, []);

  const selectValueRef = useRef<any>();
  const {
    setSelectValue,
    selectValue,
    onChange: onTopicChange,
    diffClassName: valueDiffClassName,
  } = useDefaultTwoLevelFilter({
    originalOptions: topicOptions,
    defaultValue,
    filterValue,
    beginWatch,
    onlySecondLevelValue: true,
    oriDefaultFilter,
    dealwithDefaultValue: (
      value: any,
      isFirst: boolean,
      originalOptions: any,
    ) => {
      const allOptions = getAllTopicLevelTwoValue(originalOptions);
      // 选择在话题内部的
      const selectTopic = (value?.topic || []).filter((item) =>
        allOptions.includes(item),
      );
      const allTopic = hasShortcutOptions ? allOptions : [];

      // 有时候回显没有用，可以在这打印一下
      let topic =
        selectTopic.length >= allOptions.length ? allTopic : selectTopic;

      if (hasShortcutOptions && topic.length === 0) {
        topic = allTopic;
      }

      return { value: topic, type: 'market' };
    },
  });

  // ============ 动态计算 options 和 value ============
  const currentOptions = useMemo(() => {
    if (topicType === 'category') {
      return categoryTab.categoryOptions;
    }
    return topicOptionsList;
  }, [topicType, categoryTab.categoryOptions, topicOptionsList]);

  const currentValue = useMemo(() => {
    if (topicType === 'category') {
      // categoryValue 已经是 CategoryValue 格式：{ markets, regions, type }
      const { categoryValue } = categoryTab;

      return categoryValue;
    }
    return selectValue;
  }, [topicType, categoryTab, selectValue]);

  const topicChange = (tags: any) => {
    setSelectValue(tags);
  };

  function getBackData(valueData: any) {
    const { value, oneLevel } = onTopicChange(valueData, true);
    const allTopic = getAllTopicLevelTwoValue();
    const backValue: any = { topic: value, topic_rule: topicRule, topicType };
    if (backOneLevel) backValue.topic_one_level = oneLevel;

    if (allTopic.length === value.length) {
      backValue.topic = [];
    }

    if (backAllLevel2Value || topicType === 'key_topic') {
      backValue.topic = value.length === 0 ? allTopic : value;
    }

    if (topicOptions.length === 0 && topicType === 'key_topic') {
      backValue.topic = value.length === 0 ? ['-99999'] : value;
    }

    if (topicType === 'key_topic') {
      backValue.key_topics = allTopic;
    }

    return { backValue, value, topicRule };
  }

  useEffect(() => {
    // Category Tab 处理
    if (topicType === 'category' && hasCategoryTab) {
      const backValue = categoryTab.getCategoryValue();
      onChange?.(
        'topic',
        commonBackInfo(backValue, {
          topic: categoryTab.isAll ? 'All' : categoryTab.selectedCount,
          topic_rule: 'Include',
        }),
        ['topic'],
      );
      return;
    }

    // Topic Tab（all_topic/key_topic）处理
    const { backValue, value, topicRule } = getBackData(selectValue);
    const allTopic = getAllTopicLevelTwoValue();
    const isAll = allTopic.length === value.length || value.length === 0;

    onChange?.(
      'topic',
      commonBackInfo(backValue, {
        topic: isAll ? 'All' : value.length,
        topic_rule: topicRuleTextMap[topicRule],
      }),
      ['categories'],
    );
  }, [
    selectValue,
    formatMessage,
    topicRule,
    topicType,
    topicGroupValue,
    categoryTab.categoryValue,
    hasCategoryTab,
  ]);

  const buttonElement = useMemo(() => {
    if (topicType === 'category' && hasCategoryTab) {
      // Category Tab 按钮显示
      const value = categoryTab.categoryValue.markets;

      let text: string | number = value.length;

      if (categoryTab.isAll) {
        text = formatMessage({ id: 'All' });
      } else if (value.length <= 1) {
        const option = categoryTab.categoryOptions
          .flatMap((item) => item.children || [])
          .find((child) => child.value === value[0]);

        text = (lang === 'zh-CN' ? option?.textCN : option?.text) || text;
      }

      return (
        <div>
          {hasHeaderContent && (
            <>
              <span className="select-button-text">
                {topicPlaceholder[lang]}(
                {formatMessage({ id: topicRuleTextMap.include })})
              </span>
            </>
          )}
          <span className="select-button-value">{text}</span>
        </div>
      );
    }

    // Topic Tab 按钮显示
    const { value } = onTopicChange(selectValue, true);
    const allTopic = getAllTopicLevelTwoValue();
    const isAll = allTopic.length === value.length || value.length === 0;

    let text: string | number = value.length;

    if (isAll) {
      text = formatMessage({ id: 'All' });
    } else if (value.length <= 1) {
      text = value
        .map((item: any) => topicTransMap.current[item]?.[lang])
        .join(',');
    }

    return (
      <div>
        {hasHeaderContent && (
          <>
            <span className="select-button-text">
              {topicPlaceholder[lang]}(
              {formatMessage({ id: topicRuleTextMap[topicRule] })})
            </span>
          </>
        )}
        <span className="select-button-value">{text}</span>
      </div>
    );
  }, [
    formatMessage,
    selectValue,
    topicGroupValue,
    topicRule,
    topicType,
    hasCategoryTab,
    categoryTab,
    lang,
  ]);

  const diffClassName = useMemo(() => {
    if (onlyCategory) {
      return categoryTab.isAll ? '' : 'dbo-diff-filter';
    }

    if (
      topicType !== 'all_topic' ||
      topicRule !== 'include' ||
      valueDiffClassName
    ) {
      return 'dbo-diff-filter';
    }
  }, [valueDiffClassName, topicRule, topicType, categoryTab.isAll]);

  function changeTopicRule(value: TTopicRule) {
    if (disabledOptions.length > 0) {
      const oldRule = topicRule.includes('include') ? 'include' : 'exclude';
      const newRule = value.includes('include') ? 'include' : 'exclude';

      if (oldRule !== newRule) {
        const { markets = [] } = selectValueRef.current || selectValue || {};

        if (newRule === 'include') {
          const list: string[] = markets.filter(
            (item: any) => !disabledOptions.includes(item),
          );

          setSelectValue({
            markets: list,
            regions: getLevelOneInfo(topicOptions, list),
            type: 'market',
          });
        } else {
          const list: string[] = uniqueArray([...markets, ...disabledOptions]);

          setSelectValue({
            markets: list,
            regions: getLevelOneInfo(topicOptions, list),
            type: 'market',
          });
        }
      }
    }

    setTopicRule(value);
  }

  function getBtnWrapper({
    buttonType,
  }: {
    buttonType: 'selectAll' | 'clearAll';
  }) {
    // Category Tab 不使用自定义按钮
    if (topicType === 'category' || disabledOptions.length === 0) {
      return undefined;
    }

    return function getSpanBtn(props: RegionSelectButtonContextType) {
      const allOptions = getAllTopicLevelTwoValue(topicOptionsList);
      const enabledOptions = allOptions.filter(
        (opt) => !disabledOptions.includes(opt),
      );

      const handleClick = () => {
        if (buttonType === 'selectAll') {
          let newMarkets: string[];
          if (topicRule.includes('include')) {
            newMarkets = enabledOptions;
          } else {
            newMarkets = allOptions;
          }

          const newRegions = getLevelOneInfo(topicOptions, newMarkets);
          setSelectValue({
            markets: newMarkets,
            regions: newRegions,
            type: 'market',
          });
          selectValueRef.current = {
            markets: newMarkets,
            regions: newRegions,
            type: 'market',
          };
        } else if (buttonType === 'clearAll') {
          let newMarkets: string[];
          if (topicRule.includes('include')) {
            newMarkets = [];
          } else {
            newMarkets = disabledOptions;
          }

          const newRegions = getLevelOneInfo(topicOptions, newMarkets);
          setSelectValue({
            markets: newMarkets,
            regions: newRegions,
            type: 'market',
          });
          selectValueRef.current = {
            markets: newMarkets,
            regions: newRegions,
            type: 'market',
          };
        }
      };

      return (
        <span className={props.classname} onClick={handleClick}>
          {topicRule.includes('exclude')
            ? formatMessage('Reset')
            : props.defaultText}
        </span>
      );
    };
  }
  return (
    <div
      className={`topic-select-wrapper topic-select-wrapper--topic ${diffClassName}`}
    >
      <BubbleCommon
        placement="top"
        showIcon={false}
        tipsMap={filterTopicTipsText}
      >
        <NewRegionSelect
          contentHeader={
            showSegment ? (
              <Segment
                value={topicType}
                onChange={handleTopicTypeChange}
                options={segmentOptions}
                className="dbo-tea-segment--region"
              />
            ) : undefined
          }
          header={
            <ContentHeader
              topicRule={topicType === 'category' ? 'include' : topicRule}
              setTopicRule={
                topicType === 'category' ? () => {} : changeTopicRule
              }
              topicGroupValue={
                topicType === 'category' ? 'include' : topicGroupValue
              }
              hasHeaderContent={hasHeaderContent}
              isCategoryTab={topicType === 'category'}
            ></ContentHeader>
          }
          value={currentValue}
          options={currentOptions}
          boxClassName={boxClassName}
          locale={lang}
          onChange={(value: any) => {
            selectValueRef.current = value;
          }}
          displayDisabledStatusOfSelected
          radioOptionsTips={{ global: ' ', global_wo_china: ' ' }}
          language={languageTitle}
          radioOptions={radioOptions}
          shortcutOptions={shortcutOptions}
          closeOnScroll={true}
          onCloseReginSelect={(result: any) => {
            if (topicType === 'category' && hasCategoryTab) {
              // Category Tab 的处理
              const backValue = categoryTab.handleCategoryChange(result);
              // useEffect 会自动监听状态变化并调用 onChange
              try {
                addFilterBuriedPoint(FilterButtonPoint.topic, backValue);
              } catch (error) {}
            } else {
              // Topic Tab 的处理（原有逻辑）
              topicChange(result);
              try {
                const { backValue } = getBackData(result);
                addFilterBuriedPoint(FilterButtonPoint.topic, backValue);
              } catch (error) {}
            }
          }}
          button={buttonElement}
          getCustomRegionSelectAllButton={getBtnWrapper({
            buttonType: 'selectAll',
          })}
          getCustomRegionClearAllButton={getBtnWrapper({
            buttonType: 'clearAll',
          })}
          getCustomMarketSelectAllButton={getBtnWrapper({
            buttonType: 'selectAll',
          })}
          getCustomMarketClearAllButton={getBtnWrapper({
            buttonType: 'clearAll',
          })}
        ></NewRegionSelect>
      </BubbleCommon>
    </div>
  );
}
